// ============================================================================
// BOQ — Construction cost index (rate escalation source)
// ============================================================================
// Loads boq.cost_index_yearly and exposes the per-region yearly series plus a
// memoised escalation helper. This is the construction-specific index used to
// inflation-adjust library rates onto a new BOQ's pricing date — see
// utils/escalation.js for the maths and the rationale for not using CPI.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';
import { escalateRate, escalationFactor, regionForCurrency } from '../utils/escalation';

const SELECT = 'id, series, region, year, index_value, source, note, updated_at';

export const useBoqCostIndex = (series = 'construction') => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .schema('boq')
        .from('cost_index_yearly')
        .select(SELECT)
        .eq('series', series)
        .order('region')
        .order('year');
      if (fetchErr) throw fetchErr;
      setRows((data || []).map((r) => ({
        id: r.id,
        series: r.series,
        region: r.region,
        year: r.year,
        value: Number(r.index_value),
        source: r.source,
        note: r.note,
        updatedAt: r.updated_at,
      })));
    } catch (err) {
      console.error('[useBoqCostIndex] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'cost index' }));
    } finally {
      setLoading(false);
    }
  }, [series]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Upsert one (region, year) point. Admin/supervisor only (enforced by RLS).
  const upsert = useCallback(async (entry) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) return { success: false, error: 'Please sign in.' };
      const year = parseInt(entry.year, 10);
      const value = Number(entry.index_value);
      if (!Number.isFinite(year) || !Number.isFinite(value) || value <= 0) {
        return { success: false, error: 'Enter a valid year and a positive index value.' };
      }
      const row = {
        series: entry.series || series,
        region: String(entry.region || '').trim().toUpperCase(),
        year,
        index_value: value,
        source: entry.source || 'manual',
        note: entry.note || null,
      };
      const { error: upErr } = await supabase
        .schema('boq')
        .from('cost_index_yearly')
        .upsert(row, { onConflict: 'series,region,year' });
      if (upErr) throw upErr;
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useBoqCostIndex] upsert failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'save', entity: 'cost index' }) };
    }
  }, [series, fetchAll]);

  const remove = useCallback(async (id) => {
    try {
      const { error: delErr } = await supabase
        .schema('boq')
        .from('cost_index_yearly')
        .delete()
        .eq('id', id);
      if (delErr) throw delErr;
      await fetchAll();
      return { success: true };
    } catch (err) {
      console.error('[useBoqCostIndex] delete failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'cost index' }) };
    }
  }, [fetchAll]);

  // region -> sorted [{ year, value }]
  const seriesByRegion = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.region)) map.set(r.region, []);
      map.get(r.region).push({ year: r.year, value: r.value });
    });
    return map;
  }, [rows]);

  const seriesForRegion = useCallback((region) => seriesByRegion.get(region) || [], [seriesByRegion]);

  // Escalate `value` dated `fromDate` to `toDate` (default today) for the region
  // implied by `currency`. Returns null when the index can't price it.
  const adjust = useCallback((value, fromDate, currency, toDate) => {
    const region = regionForCurrency(currency);
    const target = toDate || new Date().toISOString().slice(0, 10);
    return escalateRate(value, seriesForRegion(region), fromDate, target);
  }, [seriesForRegion]);

  const factor = useCallback((fromDate, currency, toDate) => {
    const region = regionForCurrency(currency);
    const target = toDate || new Date().toISOString().slice(0, 10);
    return escalationFactor(seriesForRegion(region), fromDate, target);
  }, [seriesForRegion]);

  const hasData = rows.length > 0;

  return { rows, loading, error, refresh: fetchAll, seriesForRegion, adjust, factor, hasData, upsert, remove };
};

export default useBoqCostIndex;
